import type { MeshClipPacket } from '@/plugins/mesh/clipping/MeshClipPacket'
import { MeshClipPacket as MeshClipPacketImpl } from '@/plugins/mesh/clipping/MeshClipPacket'
import {
  type CompiledClipState,
  createCompiledClipState,
} from '@/shared/clipping/CompiledClipState'
import type { ClipVolume } from '@/shared/types/core'
import type { ViewId } from '@/shared/types/view'

interface MeshClipScope {
  readonly state: CompiledClipState
  readonly packet: MeshClipPacket | null
}

function updatePacket(
  previous: MeshClipPacket | null | undefined,
  state: CompiledClipState
): MeshClipPacket | null {
  if (state.volumes.length === 0) {
    previous?.dispose()
    return null
  }
  if (previous) {
    previous.update(state)
    return previous
  }
  return new MeshClipPacketImpl(state)
}

/** 管理 global 与 view-scoped Mesh 裁切状态和 packet。 */
export class MeshClipStateSet {
  private globalScope: MeshClipScope
  private readonly viewScopes = new Map<ViewId, MeshClipScope>()

  constructor() {
    const state = createCompiledClipState([])
    this.globalScope = { state, packet: null }
  }

  get hasAnyVolumes(): boolean {
    if (this.globalScope.state.volumes.length > 0) return true
    for (const scope of this.viewScopes.values()) {
      if (scope.state.volumes.length > 0) return true
    }
    return false
  }

  /** 编译最新 ClipService 快照并复用仍存在 scope 的纹理。 */
  update(volumes: ReadonlyArray<ClipVolume>): number {
    const globalState = createCompiledClipState(volumes)
    this.globalScope = {
      state: globalState,
      packet: updatePacket(this.globalScope.packet, globalState),
    }

    const nextViewIds = new Set<ViewId>()
    for (const volume of volumes) {
      if (volume.viewId != null) nextViewIds.add(volume.viewId)
    }

    let droppedCount = globalState.droppedCount ?? 0
    for (const viewId of nextViewIds) {
      const state = createCompiledClipState(volumes, viewId)
      const previous = this.viewScopes.get(viewId)
      this.viewScopes.set(viewId, {
        state,
        packet: updatePacket(previous?.packet, state),
      })
      droppedCount += Math.max(0, (state.droppedCount ?? 0) - (globalState.droppedCount ?? 0))
    }

    for (const [viewId, scope] of this.viewScopes) {
      if (nextViewIds.has(viewId)) continue
      scope.packet?.dispose()
      this.viewScopes.delete(viewId)
    }

    return droppedCount
  }

  /** 未传 viewId 时只返回 global；指定 view 时返回 global + 对应 scoped 状态。 */
  get(viewId?: ViewId): MeshClipScope {
    return (viewId !== undefined ? this.viewScopes.get(viewId) : undefined) ?? this.globalScope
  }

  dispose(): void {
    this.globalScope.packet?.dispose()
    for (const scope of this.viewScopes.values()) scope.packet?.dispose()
    this.viewScopes.clear()
    const state = createCompiledClipState([])
    this.globalScope = { state, packet: null }
  }
}
